// WAAP (Eagle / SwiftFederation-Conversant) — single-vendor WAAP API surface.
//
// Backend lives in servicemgmt `modules/service/eagle_waf`, mounted at
//   /api/v3/waap/domains/:id/<area>
// so all calls go through `serviceApiV3` (base already includes /api/v3) with a
// `/waap/domains/${domainId}/${area}` path. The `:id` is the domain
// UUID; the caller's org is resolved server-side from the JWT.
//
// IMPORTANT — WAAP responses are NOT enveloped. Unlike the CDN (`{ data: T }`)
// endpoints, every WAAP GET returns the bare config object (e.g. `{ "level": 3 }`)
// and every PUT returns `{ "status": "ok" }`. So there is no `data` unwrap here.
//
// DTO shapes mirror the Go structs in `eagle_waf/types.go` verbatim (json tags) —
// WAAP field names intentionally diverge from CDN; do NOT harmonise them.

// ---- Config DTOs (mirror eagle_waf/types.go) -----------------------------

/** A single access-control rule entry (eagle_waf RuleDTO). */
export interface WaapRule {
  name: string;
  /** allow | deny (vendor accepts these; kept open for forward-compat). */
  type: string;
  /** IP CIDR or path match. */
  pattern: string;
}

/** GET/PUT /rules — access-control rules (RulesRequest). */
export interface WaapRules {
  rules: WaapRule[];
}

/** GET/PUT /mode — WAF protection-policy preset (ModeRequest). */
export interface WaapMode {
  /** Protection-policy preset integer (1-9). */
  level: number;
}

/** GET/PUT /ip-lists — account-wide IP block list (IPListsRequest). */
export interface WaapIPLists {
  /** Blacklist entries in IPv4/IPv6 CIDR notation. */
  blacklist: string[];
}

/** GET/PUT /bot-policy — bot (CC) protection (BotPolicyRequest). */
export interface WaapBotPolicy {
  /** Account-level bot-rule IDs associated with the domain. */
  rule_ids: number[];
}

/** GET/PUT /rate-limit — rate-limit policy (RateLimitRequest). */
export interface WaapRateLimit {
  /** Window in seconds. */
  time_frame: number;
  /** Max requests per window. */
  req_limit: number;
  /** Count across paths when true. */
  ignore_path: boolean;
  /** Ignore query string when true. */
  ignore_query: boolean;
}

/** GET/PUT /cors — CORS policy (CORSRequest). Header values are raw
 *  vendor strings (e.g. allow_methods "GET, POST"). */
export interface WaapCors {
  allow_origin: string;
  expose_headers: string;
  max_age: number;
  allow_credentials: boolean;
  allow_methods: string;
  allow_headers: string;
}

/** A single redirection rule (eagle_waf RedirectionDTO). When a request matches
 *  `matches` (the same req.* selector shape as access control) the edge replies
 *  with `redirection_url` + `status_code`. Lower `priority` evaluates first.
 *  Backed by the private CDN portal policy_redirections. */
export interface WaapRedirection {
  id: number;
  name: string;
  priority: number;
  enabled: boolean;
  redirection_url: string;
  /** Redirect HTTP status (301 / 302 / 307 / 308). */
  status_code: number;
  matches: WaapAccessMatch[];
}

/** GET /redirections — all redirection rules for a website. */
export interface WaapRedirections {
  rules: WaapRedirection[];
}

/** A single Website-IP-Whitelist rule (eagle_waf IPRuleDTO). */
export interface WaapIpRule {
  ip: string; // IP / start IP
  mask: string; // subnet mask / prefix length / end IP
  description: string;
  kind: string; // vendor tuple slot 4 — "mask" = IP/Subnet mask (Prefix length)
}

/** Website IP Whitelist bypass strategy. `type` selects the active list. */
export interface WaapIpStrategy {
  /** all_ip (No By-pass) | ip_white_list (By-pass IP List) | ip_protection_list (By-pass all excluding). */
  type: string;
  white_list: WaapIpRule[];
  protection_list: WaapIpRule[];
}

/** GET/PUT /site — website Configuration tab (private portal /site).
 *  Protection mode + block-response + allowed HTTP methods + IP whitelist. */
export interface WaapSiteConfig {
  /** intercept (Block) | monitor (Monitor) | passthrough (By-pass). */
  protection_mode: string;
  /** Block-response (only when intercept): reject (Block) | redirect (Redirection) | blank (Blank Page). */
  invalid_action: string;
  /** Target path when invalid_action === redirect. */
  invalid_action_redirect_path: string;
  allowed_http_methods: string[];
  ip_strategy: WaapIpStrategy;
}

/** One match condition on an access-control policy (e.g. URL Path equals ["/x"]). */
export interface WaapAccessMatch {
  what: string; // e.g. req.path
  operator: string; // e.g. equals
  patterns: string[];
}

/** A website Access-Control policy. `type` selects the section:
 *  allow (WhiteList) | deny (Blacklist) | token (Token Secret Access). */
export interface WaapAccessControl {
  id: number;
  name: string;
  type: string;
  priority: number;
  enabled: boolean;
  subnet: string; // IP/Network restriction (comma-separated)
  location: string; // GEO restriction (comma-separated ISO codes)
  anonymous_ip: boolean; // Blacklist: apply to anonymous IPs
  token_secret: string; // Token Secret Access
  matches: WaapAccessMatch[];
}

/** GET /access-control — all access-control policies for a website. */
export interface WaapAccessControls {
  rules: WaapAccessControl[];
}

/** GET /ssl — Auto Let's Encrypt certificate status. */
export interface WaapAutoCert {
  status: string; // issuing | issued | none
  request_time: string;
  cert_type: string; // none | lets_encrypt
}

/** A managed certificate in the account pool (GET /certificates). Raw vendor
 *  PortalCertificate — camelCase, ISO-8601 timestamps; expireIn = days until
 *  expiry (negative = already expired). */
export interface WaapCertificate {
  id: number;
  commonName: string;
  subjectAlternativeNames: string;
  serialNumber?: string;
  issuer: string;
  validFrom: string;
  validTo: string;
  created?: string;
  expireIn: number;
}

/** GET /certificates — raw vendor PortalCertificateList envelope. */
export interface WaapCertificates {
  size: number;
  list: WaapCertificate[];
}

/** One origin server (eagle_waf OriginEntryDTO). */
export interface WaapOriginEntry {
  protocol: string; // "https://" | "http://"
  origin: string; // host name or IPv4
  weight: number;
}

/** One custom HTTP request header forwarded to the origin. */
export interface WaapReqHeader {
  key: string;
  value: string;
  enabled: boolean;
}

/** GET/PUT /origin — website Origin tab. Persisted across the private
 *  /cdn/domain (origins + HTTP/2 + redirect) and /site (upstream + headers). */
export interface WaapOrigin {
  origins: WaapOriginEntry[];
  load_balance: string; // round_robin | ...
  enable_failover: boolean;
  keep_alive: boolean; // upstream keep-alive enable
  http2: boolean;
  redirect_http_to_https: boolean;
  // request_headers[0] is the fixed "Host" header (vendor enable_host / host_value);
  // the rest are custom headers.
  request_headers: WaapReqHeader[];
}

/** One customised response header on a cache policy. */
export interface WaapCacheResponseHeader {
  name: string;
  value: string;
  operation_type: string; // add | set | remove
}

/** A website Cache-Control policy (eagle_waf CacheBehaviourDTO). */
export interface WaapCacheBehaviour {
  id: number;
  name: string;
  priority: number;
  enabled: boolean;
  ttl: number; // cache TTL seconds; 0 = inherit origin cache TTL
  allowed_referrers: string;
  allowed_origins: string;
  ignore_client_no_cache: boolean;
  ignore_origin_no_cache: boolean;
  ignore_query_string: boolean;
  enable_x_cache: boolean;
  never_cache: boolean;
  auto_gzip_disable: boolean;
  vary_mimes: string[];
  response_headers: WaapCacheResponseHeader[];
  matches: WaapAccessMatch[];
}

/** GET /cache-control — all cache policies for a website. */
export interface WaapCacheBehaviours {
  rules: WaapCacheBehaviour[];
}

/** PUT response envelope for every WAAP write. */
/** GET /website-info — read-only basic info (incl. CNAME). */
export interface WaapWebsiteInfo {
  domain: string;
  cname: string;
  origins: WaapOriginEntry[];
  http2: boolean;
  redirect_http_to_https: boolean;
}

/** GET/PUT /multi-domains — website alternate (multi) domain names. */
export interface WaapMultiDomains {
  domains: string[];
}

export interface WaapWriteResult {
  status: string;
}

// ---- WAAP aspects --------------------------------------------------------
// The per-domain GET/PUT config areas under /<area>. Used as the path
// segment and as part of the react-query key.
export const WAAP_AREAS = {
  rules: 'rules',
  mode: 'mode',
  ipLists: 'ip-lists',
  botPolicy: 'bot-policy',
  rateLimit: 'rate-limit',
  cors: 'cors',
  redirections: 'redirections',
  site: 'site',
  websiteRateLimit: 'ratelimit',
  websiteInfo: 'website-info',
  multiDomains: 'multi-domains',
  resetSecurity: 'reset-security',
  security: 'security',
  dashboardCharts: 'dashboard/charts',
  logArchiveConfigs: 'log-archive/configs',
  logArchiveBuckets: 'log-archive/buckets',
  monitoringCharts: 'analytics/monitoring/charts',
  deliveryOverviewCharts: 'analytics/delivery-overview/charts',
  securityOverviewCharts: 'analytics/security-overview/charts',
  aiWafCharts: 'analytics/ai-waf/charts',
  botManagementCharts: 'analytics/bot-management/charts',
  apiAnalyticsCharts: 'analytics/api/charts',
  threatInvestigationCharts: 'analytics/threat-investigation/charts',
  threatInvestigationFilterOptions: 'analytics/threat-investigation/filter-options',
  threatInvestigationAccessLogDetail: 'analytics/threat-investigation/access-log/detail',
  apiProtectionOverviewCharts: 'api-protection/overview/charts',
  apiProtectionDiscoveryRules: 'api-protection/discovery/rules',
  apiProtectionDiscoveryConfig: 'api-protection/discovery/config',
  apiProtectionThreatCharts: 'api-protection/threat-detection/charts',
  apiProtectionThreats: 'api-protection/threat-detection/threats',
  apiProtectionThreatRules: 'api-protection/threat-detection/rules',
  apiProtectionDataLossRules: 'api-protection/data-loss/rules',
  apiProtectionDataLossApiAssetOptions: 'api-protection/data-loss/api-asset-options',
  apiProtectionDataLossApiAppOptions: 'api-protection/data-loss/api-app-options',
  apiProtectionVulnOverviewCharts: 'api-protection/vulnerability/overview/charts',
  apiProtectionVulnerabilities: 'api-protection/vulnerability/vulnerabilities',
  apiProtectionVulnRules: 'api-protection/vulnerability/rules',
  apiProtectionVulnAssetOptions: 'api-protection/vulnerability/api-asset-options',
  apiProtectionVulnAppMapping: 'api-protection/vulnerability/api-app-mapping',
  apiProtectionVulnAssetMapping: 'api-protection/vulnerability/api-asset-mapping',
  origin: 'origin',
  cacheControl: 'cache-control',
  accessControl: 'access-control',
  ssl: 'ssl',
  certificates: 'certificates',
  corsHeaders: 'cors-headers',
  purge: 'purge',
  prefetch: 'prefetch',
} as const;

export type WaapArea = (typeof WAAP_AREAS)[keyof typeof WAAP_AREAS];

/** Private-portal read areas (GET-only; raw vendor passthrough). */
export const WAAP_READS = {
  strategies: 'strategies',
  strategiesMapping: 'strategies/mapping',
  customStrategies: 'custom-strategies',
  aiWafStrategies: 'ai-waf-strategies',
} as const;

export type WaapRead = (typeof WAAP_READS)[keyof typeof WAAP_READS];

// ---- Paths + query keys --------------------------------------------------

/** Absolute (base-relative) path for a WAAP area/read under a domain. */
export function waapPath(domainId: string, area: WaapArea | WaapRead): string {
  return `/waap/domains/${domainId}/${area}`;
}

export const waapKeys = {
  all: ['waap'] as const,
  domain: (domainId: string) => ['waap', 'domains', domainId] as const,
  aspect: (domainId: string, area: WaapArea | WaapRead) =>
    ['waap', 'domains', domainId, area] as const,
  websites: (domainId: string) => ['waap', 'domains', domainId, 'websites'] as const,
};
